/**
 * TableField - Table/column mapping editor
 *
 * Configure table columns with key, title, and type.
 * Supports both expanded mode (column list) and collapsed mode (JSON text).
 */

import { useState, useCallback, useMemo, type ReactNode } from 'react';
import {
  makeStyles,
  tokens,
  Label,
  Text,
  Button,
  Dropdown,
  Option,
  Input,
  Tooltip,
  RadioGroup,
  Radio,
} from '@fluentui/react-components';
import {
  AddFilled,
  DeleteFilled,
  CodeFilled,
  TextFieldFilled,
} from '@fluentui/react-icons';
import { TokenizedInput } from './TokenizedInput';
import type { AgentParameterSchema } from './AgentParameterMenu';
import type { ValueSegment } from '@/types/valueSegment';
import {
  generateSegmentId,
  ValueSegmentType,
  isValueSegmentArray,
  segmentsToString,
} from '@/types/valueSegment';

import {
  fieldContainerStyle,
  fieldLabelRowStyle,
  fieldLabelStyle,
  fieldRequiredStyle,
} from './fieldStyles';

const useStyles = makeStyles({
  container: fieldContainerStyle,
  labelRow: fieldLabelRowStyle,
  label: fieldLabelStyle,
  required: fieldRequiredStyle,
  modeToggle: {
    display: 'flex',
    gap: tokens.spacingHorizontalXXS,
  },
  modeSelector: {
    marginBottom: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  modeLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: tokens.spacingVerticalXS,
  },
  columnList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  columnHeader: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 100px 32px',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  headerLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground3,
  },
  columnRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 100px 32px',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
  },
  typeSelector: {
    minWidth: '100px',
  },
  deleteButton: {
    minWidth: 'auto',
    height: '32px',
    padding: tokens.spacingHorizontalS,
  },
  addButton: {
    alignSelf: 'flex-start',
    marginTop: tokens.spacingVerticalXS,
  },
  emptyState: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
    fontStyle: 'italic',
  },
  automaticModeHint: {
    padding: tokens.spacingVerticalM,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  error: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorPaletteRedForeground1,
  },
  description: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

// Column data types
type ColumnType = 'string' | 'number' | 'boolean' | 'date' | 'any';

const COLUMN_TYPES: ColumnType[] = ['string', 'number', 'boolean', 'date', 'any'];

interface TableColumn {
  id: string;
  key: string;
  title: string;
  type: ColumnType;
}

interface TableConfig {
  mode: 'automatic' | 'custom';
  columns: TableColumn[];
}

export interface TableFieldProps {
  name: string;
  label: string;
  value: string | ValueSegment[];
  onChange: (value: string | ValueSegment[]) => void;
  description?: string | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  error?: string | undefined;
  /** Show the agent parameter button */
  showAgentParameterButton?: boolean | undefined;
  /** The agent parameter schema */
  agentParameterSchema?: AgentParameterSchema | undefined;
  /** Action outputs for previews */
  actionOutputs?: Record<string, unknown> | undefined;
  /** Whether we have run data available */
  hasRunData?: boolean | undefined;
  /** Whether the workflow has changed since last run */
  isStale?: boolean | undefined;
  /**
   * Optional slot rendered in the label row alongside the visual / JSON
   * mode toggle. Hosts inline affordances such as `FieldActionChips`.
   */
  endActions?: ReactNode | undefined;
}

/**
 * Parse table config string into TableConfig
 */
function parseTableConfig(value: string | ValueSegment[]): TableConfig {
  const stringValue = isValueSegmentArray(value)
    ? segmentsToString(value)
    : value;

  if (!stringValue || stringValue.trim() === '') {
    return { mode: 'automatic', columns: [] };
  }

  try {
    const parsed = JSON.parse(stringValue);
    return {
      mode: parsed.mode || 'automatic',
      columns: (parsed.columns || []).map((col: { key: string; title: string; type?: ColumnType }) => ({
        id: generateSegmentId(),
        key: col.key || '',
        title: col.title || '',
        type: col.type || 'string',
      })),
    };
  } catch {
    return { mode: 'automatic', columns: [] };
  }
}

/**
 * Serialize TableConfig to JSON string
 */
function serializeTableConfig(config: TableConfig): string {
  const serialized = {
    mode: config.mode,
    columns: config.columns.map((col) => ({
      key: col.key,
      title: col.title,
      type: col.type,
    })),
  };
  return JSON.stringify(serialized, null, 2);
}

export const TableField = ({
  name,
  label,
  value,
  onChange,
  description,
  required,
  disabled = false,
  error,
  showAgentParameterButton,
  agentParameterSchema,
  actionOutputs,
  hasRunData: _hasRunData,
  isStale: _isStale,
  endActions,
}: TableFieldProps) => {
  const styles = useStyles();

  // Track expanded vs collapsed mode
  const [isExpanded, setIsExpanded] = useState(true);

  // Parse value into table config
  const config = useMemo(() => parseTableConfig(value), [value]);

  // Raw JSON text for collapsed mode
  const rawJsonValue = useMemo(() => {
    const stringValue = isValueSegmentArray(value)
      ? segmentsToString(value)
      : value;
    return stringValue || '{}';
  }, [value]);

  // Update config and notify onChange
  const updateConfig = useCallback((newConfig: TableConfig) => {
    const serialized = serializeTableConfig(newConfig);
    onChange([{
      id: generateSegmentId(),
      type: ValueSegmentType.LITERAL,
      value: serialized,
    }]);
  }, [onChange]);

  // Handle mode change
  const handleModeChange = useCallback((_e: unknown, data: { value: string }) => {
    updateConfig({
      ...config,
      mode: data.value as 'automatic' | 'custom',
    });
  }, [config, updateConfig]);

  // Handle column key change
  const handleKeyChange = useCallback((colId: string, newKey: string) => {
    const newColumns = config.columns.map((col) =>
      col.id === colId ? { ...col, key: newKey } : col
    );
    updateConfig({ ...config, columns: newColumns });
  }, [config, updateConfig]);

  // Handle column title change
  const handleTitleChange = useCallback((colId: string, newTitle: string) => {
    const newColumns = config.columns.map((col) =>
      col.id === colId ? { ...col, title: newTitle } : col
    );
    updateConfig({ ...config, columns: newColumns });
  }, [config, updateConfig]);

  // Handle column type change
  const handleTypeChange = useCallback((colId: string, newType: ColumnType) => {
    const newColumns = config.columns.map((col) =>
      col.id === colId ? { ...col, type: newType } : col
    );
    updateConfig({ ...config, columns: newColumns });
  }, [config, updateConfig]);

  // Handle remove column
  const handleRemoveColumn = useCallback((colId: string) => {
    const newColumns = config.columns.filter((col) => col.id !== colId);
    updateConfig({ ...config, columns: newColumns });
  }, [config, updateConfig]);

  // Handle add column
  const handleAddColumn = useCallback(() => {
    const newColumn: TableColumn = {
      id: generateSegmentId(),
      key: `column${config.columns.length + 1}`,
      title: `Column ${config.columns.length + 1}`,
      type: 'string',
    };
    updateConfig({ ...config, columns: [...config.columns, newColumn] });
  }, [config, updateConfig]);

  // Handle raw JSON change in collapsed mode
  const handleRawChange = useCallback((segments: ValueSegment[]) => {
    onChange(segments);
  }, [onChange]);

  // Convert raw JSON to segments for TokenizedInput
  const rawJsonSegments: ValueSegment[] = useMemo(() => [{
    id: generateSegmentId(),
    type: ValueSegmentType.LITERAL,
    value: rawJsonValue,
  }], [rawJsonValue]);

  return (
    <div className={styles.container}>
      <div className={styles.labelRow}>
        <Label className={styles.label} htmlFor={name}>
          {label}
          {required && <span className={styles.required}>*</span>}
        </Label>
        <div className={styles.modeToggle}>
          {endActions}
          <Tooltip content="Visual editor" relationship="label">
            <Button
              appearance={isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<TextFieldFilled />}
              onClick={() => setIsExpanded(true)}
              disabled={disabled}
              aria-label="Visual editor"
            />
          </Tooltip>
          <Tooltip content="Edit as JSON" relationship="label">
            <Button
              appearance={!isExpanded ? 'primary' : 'subtle'}
              size="small"
              icon={<CodeFilled />}
              onClick={() => setIsExpanded(false)}
              disabled={disabled}
              aria-label="Edit as JSON"
            />
          </Tooltip>
        </div>
      </div>

      {description && (
        <Text className={styles.description}>{description}</Text>
      )}

      {isExpanded ? (
        // Expanded mode - visual editor
        <>
          {/* Mode selector */}
          <div className={styles.modeSelector}>
            <Text className={styles.modeLabel}>Column Mode</Text>
            <RadioGroup
              value={config.mode}
              onChange={handleModeChange}
              layout="horizontal"
              disabled={disabled}
            >
              <Radio value="automatic" label="Automatic" />
              <Radio value="custom" label="Custom" />
            </RadioGroup>
          </div>

          {config.mode === 'automatic' ? (
            <div className={styles.automaticModeHint}>
              <Text>
                Columns will be automatically detected from the input data.
              </Text>
            </div>
          ) : (
            // Custom mode - column list
            <div className={styles.columnList}>
              {config.columns.length === 0 ? (
                <div className={styles.emptyState}>
                  No columns defined. Click &quot;+ Add Column&quot; to add one.
                </div>
              ) : (
                <>
                  <div className={styles.columnHeader}>
                    <Text className={styles.headerLabel}>Column Key</Text>
                    <Text className={styles.headerLabel}>Display Title</Text>
                    <Text className={styles.headerLabel}>Type</Text>
                    <div /> {/* Spacer for delete button */}
                  </div>
                  {config.columns.map((col) => (
                    <div key={col.id} className={styles.columnRow}>
                      <Input
                        size="small"
                        value={col.key}
                        onChange={(_e, data) => handleKeyChange(col.id, data.value)}
                        disabled={disabled}
                        placeholder="key"
                      />
                      <Input
                        size="small"
                        value={col.title}
                        onChange={(_e, data) => handleTitleChange(col.id, data.value)}
                        disabled={disabled}
                        placeholder="Title"
                      />
                      <Dropdown
                        className={styles.typeSelector}
                        size="small"
                        value={col.type}
                        selectedOptions={[col.type]}
                        onOptionSelect={(_e, data) =>
                          handleTypeChange(col.id, data.optionValue as ColumnType)
                        }
                        disabled={disabled}
                        aria-label="Column type"
                      >
                        {COLUMN_TYPES.map((type) => (
                          <Option key={type} value={type}>
                            {type}
                          </Option>
                        ))}
                      </Dropdown>
                      <Tooltip content="Remove column" relationship="label">
                        <Button
                          appearance="subtle"
                          size="small"
                          icon={<DeleteFilled />}
                          onClick={() => handleRemoveColumn(col.id)}
                          disabled={disabled}
                          className={styles.deleteButton}
                          aria-label="Remove column"
                        />
                      </Tooltip>
                    </div>
                  ))}
                </>
              )}
              <Button
                appearance="subtle"
                size="small"
                icon={<AddFilled />}
                onClick={handleAddColumn}
                disabled={disabled}
                className={styles.addButton}
              >
                Add Column
              </Button>
            </div>
          )}
        </>
      ) : (
        // Collapsed mode - raw JSON editor
        <TokenizedInput
          name={name}
          value={rawJsonSegments}
          onChange={handleRawChange}
          placeholder='{"mode": "automatic", "columns": []}'
          multiline
          disabled={disabled}
          showAgentParameterButton={showAgentParameterButton}
          agentParameterSchema={agentParameterSchema}
          actionOutputs={actionOutputs}
        />
      )}

      {error && (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      )}
    </div>
  );
};

TableField.displayName = 'TableField';
